import { useState, useEffect, useCallback, useMemo } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  NurturingSegment,
  NurturingStep,
  StaticByDynamicTemplateMap,
  StaticStepHoverData,
} from 'app/pages/NurturingJourneySettingsComponent/types';

type RecordTypeKey = 'unsold' | 'sold';

interface ImportFromAllSegmentResult {
  success: boolean;
  message?: string;
}

function normalizeSegments(apiData: any): NurturingSegment[] {
  if (!apiData) return [];
  
  // Try to extract segments from different possible paths
  let raw: any[] = [];
  
  // Direct segments property
  if (apiData?.segments && Array.isArray(apiData.segments)) {
    raw = apiData.segments;
  }
  // data.segments path
  else if (apiData?.data?.segments && Array.isArray(apiData.data.segments)) {
    raw = apiData.data.segments;
  }
  // body.segments path
  else if (apiData?.body?.segments && Array.isArray(apiData.body.segments)) {
    raw = apiData.body.segments;
  }
  // body[0].settings[0].properties.segments path (dealer-settings format)
  else if (apiData?.body && Array.isArray(apiData.body) && apiData.body[0]?.settings?.length) {
    const settings = apiData.body[0].settings[0];
    if (settings?.properties?.segments && Array.isArray(settings.properties.segments)) {
      raw = settings.properties.segments;
    }
  }
  
  return Array.isArray(raw) ? raw : [];
}

function sortStepsByOrder(steps: NurturingStep[]): NurturingStep[] {
  return [...steps].sort((a, b) => a.step_order - b.step_order);
}

// Preserves step_order=0 for the noop step, renumbers the rest 1, 2, 3 ...
function reindexPreservingZero(steps: NurturingStep[]): NurturingStep[] {
  let counter = 1;
  return steps.map(s => {
    if (s.step_order === 0) return s;
    return { ...s, step_order: counter++ };
  });
}

export function useNurturingJourneySettings(
  apiData: any,
  staticByDynamicTemplateMap: StaticByDynamicTemplateMap = {},
) {
  const [segments, setSegments] = useState<NurturingSegment[]>(() =>
    normalizeSegments(apiData),
  );
  // Avoid double JSON.stringify of large journey configs (main-thread freeze on MyAutoIQ settings).
  const [isDirty, setIsDirty] = useState(false);

  const originalSegments = useMemo(
    () => normalizeSegments(apiData),
    [apiData],
  );

  useEffect(() => {
    setSegments(originalSegments);
    setIsDirty(false);
  }, [originalSegments]);

  const resetToOriginal = useCallback(() => {
    setSegments(originalSegments);
    setIsDirty(false);
  }, [originalSegments]);

  const reorderSteps = useCallback(
    (
      segmentIndex: number,
      recordType: RecordTypeKey,
      stageKey: string,
      oldIndex: number,
      newIndex: number,
    ) => {
      setSegments(prev => {
        const next = prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;
          const record = seg.nurturing_journey_config.record_types[recordType];
          if (!record?.nurturing_stages?.[stageKey]) return seg;
          const stage = record.nurturing_stages[stageKey];
          const sorted = sortStepsByOrder(stage.steps);
          const reordered = arrayMove(sorted, oldIndex, newIndex);
          const reindexed = reindexPreservingZero(reordered);
          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: {
                ...seg.nurturing_journey_config.record_types,
                [recordType]: {
                  ...record,
                  nurturing_stages: {
                    ...record.nurturing_stages,
                    [stageKey]: { ...stage, steps: reindexed },
                  },
                },
              },
            },
          };
        });
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  const updateStep = useCallback(
    (
      segmentIndex: number,
      recordType: RecordTypeKey,
      stageKey: string,
      stepIndex: number,
      patch: Partial<NurturingStep>,
    ) => {
      setSegments(prev => {
        const next = prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;
          const record = seg.nurturing_journey_config.record_types[recordType];
          if (!record?.nurturing_stages?.[stageKey]) return seg;
          const stage = record.nurturing_stages[stageKey];
          const sorted = sortStepsByOrder(stage.steps);
          const steps = sorted.map((s, i) =>
            i === stepIndex ? { ...s, ...patch } : s,
          );
          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: {
                ...seg.nurturing_journey_config.record_types,
                [recordType]: {
                  ...record,
                  nurturing_stages: {
                    ...record.nurturing_stages,
                    [stageKey]: { ...stage, steps },
                  },
                },
              },
            },
          };
        });
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  const updateMaxRepeat = useCallback(
    (
      segmentIndex: number,
      recordType: RecordTypeKey,
      stageKey: string,
      value: number | null,
    ) => {
      setSegments(prev => {
        const next = prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;
          const record = seg.nurturing_journey_config.record_types[recordType];
          if (!record?.nurturing_stages?.[stageKey]) return seg;
          const stage = record.nurturing_stages[stageKey];
          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: {
                ...seg.nurturing_journey_config.record_types,
                [recordType]: {
                  ...record,
                  nurturing_stages: {
                    ...record.nurturing_stages,
                    [stageKey]: { ...stage, max_repeat: value },
                  },
                },
              },
            },
          };
        });
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  const addStep = useCallback(
    (
      segmentIndex: number,
      recordType: RecordTypeKey,
      stageKey: string,
      newStep: NurturingStep,
    ) => {
      setSegments(prev =>
        prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;
          const record = seg.nurturing_journey_config.record_types[recordType];
          if (!record?.nurturing_stages?.[stageKey]) return seg;
          const stage = record.nurturing_stages[stageKey];
          const maxOrder = stage.steps.reduce((m, s) => Math.max(m, s.step_order), 0);
          const stepToAdd = { ...newStep, step_order: maxOrder + 1 };
          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: {
                ...seg.nurturing_journey_config.record_types,
                [recordType]: {
                  ...record,
                  nurturing_stages: {
                    ...record.nurturing_stages,
                    [stageKey]: { ...stage, steps: [...stage.steps, stepToAdd] },
                  },
                },
              },
            },
          };
        }),
      );
      setIsDirty(true);
    },
    [],
  );

  const deleteStep = useCallback(
    (
      segmentIndex: number,
      recordType: RecordTypeKey,
      stageKey: string,
      stepIndex: number,
    ) => {
      setSegments(prev =>
        prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;
          const record = seg.nurturing_journey_config.record_types[recordType];
          if (!record?.nurturing_stages?.[stageKey]) return seg;
          const stage = record.nurturing_stages[stageKey];
          const sorted = sortStepsByOrder(stage.steps);
          const filtered = sorted.filter((_, i) => i !== stepIndex);
          const reindexed = reindexPreservingZero(filtered);
          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: {
                ...seg.nurturing_journey_config.record_types,
                [recordType]: {
                  ...record,
                  nurturing_stages: {
                    ...record.nurturing_stages,
                    [stageKey]: { ...stage, steps: reindexed },
                  },
                },
              },
            },
          };
        }),
      );
      setIsDirty(true);
    },
    [],
  );

  const updateSegmentOverride = useCallback(
    (segmentIndex: number, value: boolean) => {
      setSegments(prev =>
        prev.map((seg, si) => {
          if (si !== segmentIndex || seg.segment_id === 'ALL') return seg;
          return { ...seg, override: value };
        }),
      );
      setIsDirty(true);
    },
    [],
  );

  const importStepsFromAllSegment = useCallback(
    (segmentIndex: number): ImportFromAllSegmentResult => {
      if (segmentIndex < 0 || segmentIndex >= segments.length) {
        return {
          success: false,
          message: 'Unable to import steps: invalid segment selected.',
        };
      }

      const targetSegment = segments[segmentIndex];
      if (!targetSegment) {
        return {
          success: false,
          message: 'Unable to import steps: selected segment not found.',
        };
      }

      if (String(targetSegment.segment_id).toUpperCase() === 'ALL') {
        return {
          success: false,
          message: 'Please select a segment other than ALL to import steps.',
        };
      }

      const allSegment = segments.find(
        seg => String(seg.segment_id).toUpperCase() === 'ALL',
      );

      if (!allSegment?.nurturing_journey_config?.record_types) {
        return {
          success: false,
          message: 'Unable to import steps: ALL segment steps were not found.',
        };
      }

      const clonedRecordTypes = JSON.parse(
        JSON.stringify(allSegment.nurturing_journey_config.record_types),
      );

      setSegments(prev =>
        prev.map((seg, si) => {
          if (si !== segmentIndex) return seg;

          return {
            ...seg,
            nurturing_journey_config: {
              ...seg.nurturing_journey_config,
              record_types: clonedRecordTypes,
            },
          };
        }),
      );
      setIsDirty(true);

      return { success: true };
    },
    [segments],
  );

  const allStaticTemplateTypes = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(staticByDynamicTemplateMap)
            .flat()
            .filter(v => typeof v === 'string'),
        ),
      ),
    [staticByDynamicTemplateMap],
  );

  const getStaticStepHoverData = useCallback(
    (steps: NurturingStep[], staticStepIndex: number): StaticStepHoverData => {
      if (!steps[staticStepIndex] || steps[staticStepIndex].generated_template_type !== 'static') {
        return { previousDynamicType: null, allowedStaticTypes: [], mappedByDynamicType: {} };
      }

      let previousDynamicTemplateTypes: string[] = [];
      for (let i = staticStepIndex - 1; i >= 0; i -= 1) {
        const candidate = steps[i];
        if (candidate.generated_template_type === 'dynamic') {
          const fromTemplateTypes = Array.isArray((candidate as any).template_types)
            ? (candidate as any).template_types.filter((v: unknown): v is string => typeof v === 'string')
            : [];
          const fromAlternateOptions = Array.isArray((candidate as any).alternate_options)
            ? (candidate as any).alternate_options.filter((v: unknown): v is string => typeof v === 'string')
            : [];
          previousDynamicTemplateTypes = Array.from(
            new Set([...fromTemplateTypes, ...fromAlternateOptions]),
          );
          break;
        }
      }

      if (previousDynamicTemplateTypes.length === 0) {
        return {
          previousDynamicType: null,
          allowedStaticTypes: allStaticTemplateTypes,
          mappedByDynamicType: staticByDynamicTemplateMap,
        };
      }

      const mappedByDynamicType = previousDynamicTemplateTypes.reduce<Record<string, string[]>>(
        (acc, dynamicTemplateType) => {
          acc[dynamicTemplateType] = staticByDynamicTemplateMap[dynamicTemplateType] ?? [];
          return acc;
        },
        {},
      );

      const resolvedStaticTypes = Array.from(
        new Set(
          previousDynamicTemplateTypes.flatMap(
            dynamicTemplateType => staticByDynamicTemplateMap[dynamicTemplateType] ?? [],
          ),
        ),
      );

      return {
        previousDynamicType: previousDynamicTemplateTypes.join(', '),
        allowedStaticTypes:
          resolvedStaticTypes.length > 0 ? resolvedStaticTypes : allStaticTemplateTypes,
        mappedByDynamicType,
      };
    },
    [allStaticTemplateTypes, staticByDynamicTemplateMap],
  );

  return {
    segments,
    originalSegments,
    isDirty,
    resetToOriginal,
    reorderSteps,
    updateStep,
    updateMaxRepeat,
    addStep,
    deleteStep,
    updateSegmentOverride,
    importStepsFromAllSegment,
    getStaticStepHoverData,
  };
}
